import styled from '@emotion/styled'
import {
    ApiShopperLandingPageLink,
    buildOptionKeyMap,
    formatVariantOptionValues,
    VLPVariant,
} from '@storefronts/common-types'
import Image from 'next/image'
import React, { useContext } from 'react'
import {
    PRIMARY_FONT_CSS_VAR,
    PRIMARY_DISCOUNT_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
} from '../../context/CustomizationContextProvider'
import { ViddyLPContext } from '../../context/viddyLPContext'
import { DEFAULT_LOCALE, MIN_VISIBLE_REVIEW_COUNT } from '../../constants'
import { getTypographyRoleStyle } from '../../style/typography'
import { getReviewSummary } from '../../utils/getReviewSummary'
import { imageUrlTransform } from '../../utils/imageUrlTransform'
import { priceFormat, type Prices } from '../../utils/priceFormat'
import { getFloatingSummaryCompareAtPrice } from '../../utils/floatingProductSummary'
import {
    getDisplayData,
    resolveProductCardImageUrl,
} from '../../utils/productDisplay'
import { ReviewStar } from '../landing-page/reviews/ReviewStar'
import { scaledFontSize } from '../../style/scrollingLayout'
const ProductTitle = styled.div({
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    ...getTypographyRoleStyle('productTitle', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 400,
        lineHeight: 1.25,
    }),
    fontSize: scaledFontSize(14),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
})

const SummaryContainer = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    width: '100%',
    paddingBottom: 8,
    cursor: 'pointer',
    boxSizing: 'border-box',
})

const ImageFrame = styled.div({
    position: 'relative',
    flex: '0 0 52px',
    width: 52,
    height: 52,
    borderRadius: '0.2rem',
    overflow: 'hidden',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
})

const ProductText = styled.div({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: '1 1 auto',
})

const RatingRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 3,
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
})

const RatingText = styled.span({
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    ...getTypographyRoleStyle('captionText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 400,
        lineHeight: 1,
    }),
    fontSize: scaledFontSize(12),
})

const ReviewCount = styled(RatingText)({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
})

const VariantValues = styled.div({
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    ...getTypographyRoleStyle('captionText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 400,
        lineHeight: 1.2,
    }),
    fontSize: scaledFontSize(12),
    marginTop: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
})

const PriceBlock = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flex: '0 0 auto',
    lineHeight: 1,
    whiteSpace: 'nowrap',
})

const PriceMainRow = styled.div({
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'flex-end',
    gap: 8,
})

const DiscountText = styled.span({
    color: `var(${PRIMARY_DISCOUNT_COLOR_VAR})`,
    ...getTypographyRoleStyle('badgeText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 400,
        lineHeight: 1,
    }),
    fontSize: scaledFontSize(20),
})

const CurrentPrice = styled.span({
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    ...getTypographyRoleStyle('price', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 400,
        lineHeight: 1,
    }),
    fontSize: scaledFontSize(17),
})

const CompareAtPrice = styled.span({
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    ...getTypographyRoleStyle('compareAtPrice', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 400,
        lineHeight: 1,
    }),
    fontSize: scaledFontSize(13),
    marginTop: 7,
    textDecoration: 'line-through',
})

type FloatingProductSummaryProps = {
    lpLink: ApiShopperLandingPageLink
    selectedVariant: VLPVariant
    priceInfo: Prices
    onClick: () => void
}

export const FloatingProductSummary: React.FC<FloatingProductSummaryProps> = ({
    lpLink,
    selectedVariant,
    priceInfo,
    onClick,
}) => {
    const { landingPage } = useContext(ViddyLPContext)
    const displayData = getDisplayData(
        lpLink,
        selectedVariant.shopifyProductId ?? null,
    )
    const image = imageUrlTransform(
        resolveProductCardImageUrl(selectedVariant, lpLink, displayData),
        'thumbnail',
    )
    const displayTitle = displayData.title ?? lpLink.title
    const optionInfo = (lpLink.optionInfo || []).slice(0, 3)
    const variantValues = formatVariantOptionValues(
        selectedVariant,
        optionInfo,
        buildOptionKeyMap(optionInfo),
    )
    const { reviewRating, reviewCount } = getReviewSummary(landingPage, {
        ...lpLink,
        reviews: displayData.reviews ?? lpLink.reviews,
    })
    const showRating =
        reviewRating != null &&
        reviewCount != null &&
        reviewCount > MIN_VISIBLE_REVIEW_COUNT
    const hasDiscount =
        priceInfo.discountPercentage != null && priceInfo.discountPercentage > 0
    const compareAtPrice = getFloatingSummaryCompareAtPrice(priceInfo)
    const showCompareAtPrice = compareAtPrice != null
    const currencySymbol = landingPage?.client?.currencySymbol || '$'

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onClick()
        }
    }

    return (
        <SummaryContainer
            onClick={onClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
        >
            <ImageFrame>
                <Image
                    src={image}
                    alt={displayTitle || selectedVariant.title || 'Product'}
                    fill
                    sizes="52px"
                    style={{
                        objectFit: 'contain',
                        objectPosition: 'center center',
                    }}
                />
            </ImageFrame>
            <ProductText>
                <ProductTitle>{displayTitle}</ProductTitle>
                {showRating && (
                    <RatingRow>
                        <RatingText>{reviewRating.toFixed(1)}</RatingText>
                        <ReviewStar rating={reviewRating} starWidth={12} />
                        <ReviewCount>
                            ({reviewCount.toLocaleString(DEFAULT_LOCALE)})
                        </ReviewCount>
                    </RatingRow>
                )}
                {variantValues && (
                    <VariantValues>{variantValues}</VariantValues>
                )}
            </ProductText>
            <PriceBlock>
                <PriceMainRow>
                    {hasDiscount && (
                        <DiscountText>
                            -{priceInfo.discountPercentage}%
                        </DiscountText>
                    )}
                    <CurrentPrice>
                        {priceFormat(priceInfo.price, currencySymbol)}
                    </CurrentPrice>
                </PriceMainRow>
                {showCompareAtPrice && (
                    <CompareAtPrice>
                        {priceFormat(compareAtPrice, currencySymbol)}
                    </CompareAtPrice>
                )}
            </PriceBlock>
        </SummaryContainer>
    )
}
